import { PRODUCTS } from '../data';
import { CITIES } from '../cities';
import { EVENT_TYPES } from '../eventTypes';

/**
 * Fuente única de <title> y <meta description> de cada ruta.
 *
 * Existe por un problema concreto: el sitio es una SPA y `useSEO` escribe los
 * meta con JavaScript. WhatsApp, Facebook y el resto de crawlers de
 * previsualización NO ejecutan JavaScript, así que sin esto todas las URLs
 * compartirían el preview de la home. `scripts/prerender.mjs` lee este mismo
 * módulo durante el build y deja los meta ya escritos en un HTML por ruta.
 *
 * Las páginas consumen ROUTE_META con spread, de modo que lo que ve el crawler
 * y lo que ve el navegador salen del mismo sitio y no pueden desincronizarse.
 */
export interface RouteMeta {
  title: string;
  description: string;
}

export interface PrerenderRoute extends RouteMeta {
  path: string;
  ogImage?: string;
  noindex?: boolean;
}

/** Rutas estáticas. Las dinámicas se derivan de PRODUCTS y CITIES más abajo. */
export const ROUTE_META = {
  '/': {
    title: 'Cabina de Fotos | Alquiler para Bodas y Eventos',
    description:
      'Alquilamos cabinas de fotos para bodas, grados, cumpleaños y eventos de empresa en Colombia. Impresión ilimitada, operador incluido y galería digital.',
  },
  '/catalogo': {
    title: 'Catálogo de Cabinas de Fotos | 16 Modelos',
    description:
      'Todos los modelos en alquiler: cabinas con impresión, espejos mágicos, plataforma 360, Glambot y spots fotográficos. Ficha técnica y espacio de cada uno.',
  },
  '/eventos': {
    title: 'Cabina de Fotos por Tipo de Evento | 6 Formatos',
    description:
      'Bodas, quince años, cumpleaños, empresa, activaciones de marca y reuniones familiares: cómo montamos la cabina en cada uno y qué equipo rinde mejor.',
  },
  '/venta': {
    title: 'Venta de Cabinas de Fotos en Colombia | Fabricantes',
    description:
      'Compra tu cabina de fotos o plataforma 360 armada, con software de licencia vitalicia, capacitación, garantía de un año y soporte técnico en Colombia.',
  },
  '/precios': {
    title: 'Precios de Alquiler de Cabina de Fotos',
    description:
      'Qué determina el precio de alquilar una cabina de fotos en Colombia: modelo, horas, ciudad e invitados. Mira qué va incluido y qué se cotiza aparte.',
  },
  '/ciudades': {
    title: 'Dónde Alquilamos Cabinas de Fotos en Colombia',
    description:
      'Alquiler de cabinas de fotos en Bogotá, Medellín, Cali, Barranquilla, Cartagena, Bucaramanga y Pereira, más los municipios cercanos a cada ciudad.',
  },
  '/contacto': {
    title: 'Contacto | Cotiza tu Cabina de Fotos',
    description:
      'Escríbenos para cotizar tu cabina de fotos. Con la fecha, la ciudad y el tipo de evento te enviamos el valor el mismo día por WhatsApp.',
  },
  '/404': {
    title: 'Página no encontrada | Cabina de Fotos',
    description:
      'La página que buscas no existe o cambió de dirección. Mira el catálogo de cabinas de fotos y pide tu cotización.',
  },
} satisfies Record<string, RouteMeta>;

export const ALL_ROUTES: PrerenderRoute[] = [
  ...Object.entries(ROUTE_META).map(([path, meta]) => ({
    path,
    ...meta,
    noindex: path === '/404',
  })),
  ...PRODUCTS.map((p) => ({
    path: `/catalogo/${p.id}`,
    /* `seoTitle` ya viene completo y con su propio sufijo descriptivo:
       añadirle la marca produce un doble sufijo que Google trunca. */
    title: p.seoTitle,
    description: p.seoDescription,
    ogImage: p.image,
  })),
  ...EVENT_TYPES.map((e) => ({
    path: `/eventos/${e.slug}`,
    title: e.seoTitle,
    description: e.seoDescription,
    ogImage: e.heroImage,
  })),
  ...CITIES.map((c) => ({
    path: `/ciudades/${c.slug}`,
    title: c.seoTitle,
    description: c.seoDescription,
  })),
];
